import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit3, FiUser, FiBriefcase, FiDollarSign } from 'react-icons/fi';
import { vendedoresService } from '../../services';

const VendedorDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendedor, setVendedor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Opciones para los selectores
  const roles = [
    { value: 'sales', label: 'VENDEDOR' },
    { value: 'sales_manager', label: 'GERENTE DE VENTAS' },
    { value: 'manager', label: 'GERENTE' },
    { value: 'admin', label: 'ADMINISTRADOR' },
    { value: 'support', label: 'SOPORTE' }
  ];

  const frecuenciasPago = [
    { value: 'WEEKLY', label: 'SEMANAL' },
    { value: 'BIWEEKLY', label: 'CADA 15 DÍAS' },
    { value: 'MONTHLY', label: 'CADA FINAL DE MES' },
    { value: 'OTHER', label: 'OTRO' }
  ];

  const metodosPago = [
    { value: 'ZELLE', label: 'ZELLE' },
    { value: 'CHECK', label: 'CHEQUE' },
    { value: 'BANK_TRANSFER', label: 'DEPÓSITO/TRANSFERENCIA' },
    { value: 'CASH', label: 'EFECTIVO' },
    { value: 'OTHER', label: 'OTROS' }
  ];

  const paises = [
    { value: 'Colombia', label: 'Colombia' },
    { value: 'Estados Unidos', label: 'Estados Unidos' },
    { value: 'Canadá', label: 'Canadá' },
    { value: 'México', label: 'México' }
  ];

  const idiomas = [
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' }
  ];

  const timezones = [
    { value: 'America/Bogota', label: 'Bogotá (GMT-5)' },
    { value: 'America/New_York', label: 'New York (GMT-5)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8)' },
    { value: 'America/Mexico_City', label: 'Ciudad de México (GMT-6)' }
  ];

  const loadVendedorDetalle = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await vendedoresService.getVendedor(id);

      if (result.success) {
        setVendedor(result.data);
      } else {
        setError(result.message || 'Error al cargar el vendedor');
      }
    } catch (err) {
      console.error('Error al cargar vendedor:', err);
      setError('Error al cargar el vendedor');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadVendedorDetalle();
  }, [loadVendedorDetalle]);

  const getLabelFromOptions = (options, value) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value || 'N/A';
  };

  if (isLoading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner-single" style={{ width: '40px', height: '40px' }}></div>
        <div className="loading-message">Cargando vendedor...</div>
      </div>
    );
  }

  if (error || !vendedor) {
    return (
      <div className="cliente-form-page">
        <div className="form-header">
          <button
            className="btn btn--ghost"
            onClick={() => navigate('/system/vendedores')}
            type="button"
          >
            <FiArrowLeft /> Volver a Vendedores
          </button>
        </div>
        <div className="alert alert--danger">
          {error || 'Vendedor no encontrado'}
        </div>
      </div>
    );
  }

  return (
    <div className="cliente-form-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/vendedores')}
          type="button"
        >
          <FiArrowLeft /> Volver a Vendedores
        </button>

        <h1 className="form-title">
          DETALLE DE VENDEDOR
        </h1>

        <div className="form-actions">
          <button
            className="btn btn--primary"
            onClick={() => navigate(`/system/vendedores/${id}/editar`)}
            type="button"
          >
            <FiEdit3 /> Editar Vendedor
          </button>
        </div>
      </div>

      {/* Formulario (campos deshabilitados) */}
      <div className="cliente-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Sección: Información Personal */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Información Personal</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre Completo</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.nombre || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={vendedor.email || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input
                  type="tel"
                  className="form-input"
                  value={vendedor.telefono || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Dirección</label>
                <textarea
                  className="form-input"
                  value={vendedor.direccion || ''}
                  disabled
                  readOnly
                  rows={3}
                />
              </div>
            </div>

            {/* Sección: Información Bancaria */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Información Bancaria</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre del Banco</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.nombreBanco || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Número de Cuenta</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.numeroCuenta || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Cuenta</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.tipoCuenta || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Routing Number</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.routingNumber || ''}
                  disabled
                  readOnly
                />
              </div>
            </div>

            {/* Sección: Preferencias */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Preferencias</h3>
              </div>

              <div className="form-group">
                <label className="form-label">País</label>
                <input
                  type="text"
                  className="form-input"
                  value={getLabelFromOptions(paises, vendedor.pais)}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Idioma</label>
                <input
                  type="text"
                  className="form-input"
                  value={getLabelFromOptions(idiomas, vendedor.idioma)}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Zona Horaria</label>
                <input
                  type="text"
                  className="form-input"
                  value={getLabelFromOptions(timezones, vendedor.timezone)}
                  disabled
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Sección: Información Laboral */}
            <div className="form-section">
              <div className="section-header">
                <FiBriefcase className="section-icon" />
                <h2 className="section-title">Información Laboral</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Rol</label>
                <input
                  type="text"
                  className="form-input"
                  value={getLabelFromOptions(roles, vendedor.rol)}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Fecha de Ingreso</label>
                <input
                  type="date"
                  className="form-input"
                  value={vendedor.fechaIngreso ? new Date(vendedor.fechaIngreso).toISOString().split('T')[0] : ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Departamento</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.departamento || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Estado</label>
                <div className="form-input" style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#f9fafb'
                }}>
                  <span className={`status-badge ${vendedor.estado === 'activo' ? 'status-badge--success' : 'status-badge--danger'}`}>
                    {vendedor.estado === 'activo' ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </div>
              </div>
            </div>

            {/* Sección: Compensación */}
            <div className="form-section">
              <div className="section-header">
                <FiDollarSign className="section-icon" />
                <h2 className="section-title">Compensación</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Salario Base Mensual</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.salarioBase ? parseFloat(vendedor.salarioBase).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    disabled
                    readOnly
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Comisión (%)</label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.comisionPorcentaje || '0'}
                    disabled
                    readOnly
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Frecuencia de Pago</label>
                <input
                  type="text"
                  className="form-input"
                  value={getLabelFromOptions(frecuenciasPago, vendedor.frecuenciaPago)}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Método de Pago</label>
                <input
                  type="text"
                  className="form-input"
                  value={getLabelFromOptions(metodosPago, vendedor.metodoPago)}
                  disabled
                  readOnly
                />
              </div>
            </div>

            {/* Sección: Métricas de Desempeño */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Métricas de Desempeño</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Clientes Asignados</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.clientesAsignados || '0'}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Ventas Este Mes</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.ventasMes ? parseFloat(vendedor.ventasMes).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    disabled
                    readOnly
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Total Ventas</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.ventasTotal ? parseFloat(vendedor.ventasTotal).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    disabled
                    readOnly
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Meta Mensual</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.metaMensual ? parseFloat(vendedor.metaMensual).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Sección: Notas */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Notas</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Notas Adicionales</label>
                <textarea
                  className="form-input"
                  value={vendedor.notas || ''}
                  disabled
                  readOnly
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendedorDetalle;
